if (typeof PredictiveSearch !== 'function') {
	class PredictiveSearch extends HTMLElement {
		constructor() {
			super();

			this.cachedResults = {};
			this.isOpen = false;
			this.isEnabled = this.dataset.predictiveSearchEnabled === 'true' ? true : false;

			this.selectors = {
				form: 'form.predictive-search__form',
				input: 'input[type="search"]',
				predictiveSearchResults: '[data-predictive-search]',
				triggerSearch: '.links__link--trigger-search',
				closeButton: '.form__close-button',
				predictiveSearchTabs: '.predictive-search__tabs',
				predictiveSearchTabsLinks: '.predictive-search__tabs a',
				tabsContent: '.tabs-content__results-list',
				tabsFilter: '.tabs-content__filter',
				recentlyViewedDelete: '.recently-viewed__delete',
				suggestedSearches: '.content__suggested-searches',
				recentlyViewedWrapper: '.content__recently-viewed',
				open: '.open',
				tabsMenu: '.predictive-search__tabs-menu',
				themeHeader: 'theme-header',
				headerGroup: '.shopify-section-group-header-group',
				header: '.header',
				templateSearch: '.template-search',
				buttonMore: '.tabs-content__button--more',
				buttonLess: '.tabs-content__button--less',
			};

			this.elements = {
				form: this.querySelector(this.selectors.form),
				input: this.querySelector(this.selectors.input),
				predictiveSearchResults: this.querySelector(this.selectors.predictiveSearchResults),
				triggerSearch: document.querySelector(this.selectors.triggerSearch),
				closeButton: this.querySelector(this.selectors.closeButton),
				recentlyViewedDelete: this.querySelectorAll(this.selectors.recentlyViewedDelete),
				headerGroupElements: document.querySelectorAll(this.selectors.headerGroup),
				header: document.querySelector(this.selectors.themeHeader + ' ' + this.selectors.header),
				templateSearch: document.querySelector(this.selectors.templateSearch),
			};

			this.classes = {
				transition: 'predictive-search--transition',
				active: 'active',
				open: 'open',
				filter: 'tabs-content__filter',
				hidden: 'hidden',
				buttonMore: 'tabs-content__button--more',
				buttonLess: 'tabs-content__button--less',
				recentlyViewedDelete: 'recently-viewed__delete',
				blockedScroll: 'blocked-scroll',
			};

			// Temporary blocker until search page is ready
			if (!this.elements.templateSearch) {
				this.init();
			}
		}

		init() {
			this.setupEventListeners();
		}

		addTransition() {
			this.classList.add(this.classes.transition);
		}

		renderInitialContent() {
			this.initialContent = this.generateViewedProductsMarkup();
			this.elements.predictiveSearchResults.innerHTML = this.initialContent;
		}

		generateViewedProductsMarkup() {
			const themeViewedProducts = JSON.parse(localStorage.getItem('themeViewedProducts')) || [];

			if (!themeViewedProducts || themeViewedProducts.length === 0) {
				if (this.querySelector(this.selectors.suggestedSearches)) {
					return this.querySelector(this.selectors.suggestedSearches).outerHTML;
				} else {
					return '';
				}
			}

			const recentlyViewedWrapper = this.querySelector(this.selectors.recentlyViewedWrapper);

			if (recentlyViewedWrapper) {
				const viewedProductsMarkup =
					themeViewedProducts
						.slice(0, 10)
						.map((product) => {
							return `<li role="option">
										<a href="${window.routes.root}products/${product.handle}" class="content__results-link button button--link">
											${product.title}
										</a>
									</li>`;
						})
						.join('') || '';
				recentlyViewedWrapper.querySelector('ul').innerHTML = viewedProductsMarkup;

				if (this.querySelector(this.selectors.suggestedSearches) !== null) {
					return (
						recentlyViewedWrapper.outerHTML + this.querySelector(this.selectors.suggestedSearches).outerHTML
					);
				} else {
					return recentlyViewedWrapper.outerHTML;
				}
			} else {
				if (this.querySelector(this.selectors.suggestedSearches) !== null) {
					return this.querySelector(this.selectors.suggestedSearches).outerHTML;
				} else {
					return '';
				}
			}
		}

		setupEventListeners() {
			window.addEventListener('load', () => {
				this.addTransition();

				if (this.isEnabled) {
					this.renderInitialContent();
				}
			});

			if (this.elements.triggerSearch) {
				this.elements.triggerSearch.addEventListener('click', (event) => {
					event.preventDefault();

					if (!this.isEnabled) {
						this.calculateHeight();
					}

					this.onTriggerSearch();
				});
			}

			if (this.elements.closeButton) {
				this.elements.closeButton.addEventListener('click', (event) => {
					if (this.elements.input.value == '') {
						this.onTriggerSearch();
					}
				});
			}

			document.addEventListener('keydown', (event) => {
				if (
					(event.key === 'Escape' && this.isOpen === true) ||
					(event.keyCode === 27 && this.isOpen === true)
				) {
					this.onTriggerSearch();
				}
			});

			if (!this.elements.form || !this.isEnabled) return;

			this.elements.form.addEventListener('submit', this.onFormSubmit.bind(this));
			this.elements.input.addEventListener('focus', this.onFocus.bind(this));
			this.elements.input.addEventListener(
				'input',
				debounce((event) => {
					this.onChange(event);
				}, 600).bind(this)
			);

			this.addEventListener('click', (event) => {
				const targetClassList = event.target.classList;

				if (event.target.dataset.tab && event.target.dataset.tab !== '') {
					event.preventDefault();
					this.querySelectorAll(this.selectors.predictiveSearchTabsLinks).forEach((link) =>
						link.classList.remove(this.classes.active)
					);
					this.querySelectorAll('[data-tab]').forEach((link) => link.classList.remove(this.classes.open));
					this.querySelectorAll(`[data-tab="${event.target.dataset.tab}"]`).forEach((link) =>
						link.classList.add(this.classes.open)
					);
					event.target.classList.add(this.classes.active);
				}

				if (targetClassList.contains(this.classes.filter)) {
					event.preventDefault();

					if (!event.target.classList.contains(this.classes.active)) {
						this.querySelectorAll(this.selectors.tabsFilter).forEach((link) =>
							link.classList.remove(this.classes.active)
						);

						event.target.classList.add(this.classes.active);

						this.querySelectorAll(`${this.selectors.tabsContent}${this.selectors.open} > li`).forEach(
							(product) => {
								if (
									product.dataset.filtering.includes(event.target.dataset.filtering) ||
									event.target.dataset.filtering === 'all'
								) {
									product.classList.remove(this.classes.hidden);
								} else {
									product.classList.add(this.classes.hidden);
								}
							}
						);
					}
				}

				if (targetClassList.contains(this.classes.buttonMore)) {
					this.querySelectorAll(this.selectors.tabsFilter).forEach((link) =>
						link.classList.remove(this.classes.hidden)
					);

					event.target.classList.add(this.classes.hidden);
					event.target.parentElement
						.querySelector(this.selectors.buttonLess)
						.classList.remove(this.classes.hidden);

					return;
				}

				if (targetClassList.contains(this.classes.buttonLess)) {
					this.querySelectorAll(this.selectors.tabsFilter).forEach((link, index) => {
						if (index > 4) {
							link.classList.add(this.classes.hidden);
						}
					});

					event.target.classList.add(this.classes.hidden);
					event.target.parentElement
						.querySelector(this.selectors.buttonMore)
						.classList.remove(this.classes.hidden);

					return;
				}

				if (
					targetClassList.contains(this.classes.recentlyViewedDelete) ||
					event.target.closest(this.selectors.recentlyViewedDelete)
				) {
					event.preventDefault();

					const recentlyViewedEvent = new CustomEvent('recently-viewed:clear');

					document.dispatchEvent(recentlyViewedEvent);
					event.target.closest(this.selectors.recentlyViewedWrapper).remove();
				}
			});
		}

		calculateHeight() {
			let height = 0;

			if (getComputedStyle(this.elements.header).position === 'fixed') {
				height = this.elements.header.offsetHeight;
			} else {
				this.elements.headerGroupElements.forEach((headerGroup) => {
					height += headerGroup.offsetHeight;
				});
			}

			this.style.setProperty('--header-group-height', `${height}px`);
		}

		onTriggerSearch() {
			if (this.isOpen) {
				this.classList.remove(this.classes.open);

				document.body.classList.remove(this.classes.blockedScroll);

				this.close();
			} else {
				this.classList.add(this.classes.open);

				document.body.classList.add(this.classes.blockedScroll);

				this.elements.input.focus();
				this.open();
			}
		}

		onChange() {
			const searchTerm = this.elements.input.value.trim();

			if (!searchTerm.length) {
				this.close(true);

				return;
			}

			this.getSearchResults(searchTerm);
		}

		onFormSubmit(event) {
			if (!this.elements.input.value.trim().length || this.querySelector('[aria-selected="true"] a'))
				event.preventDefault();
		}

		onFocus() {
			const searchTerm = this.elements.input.value.trim();

			if (!searchTerm.length) return;

			if (this.getAttribute('results') === 'true') {
				this.open();
			} else {
				this.getSearchResults(searchTerm);
			}
		}

		getSearchResults(searchTerm) {
			const queryKey = searchTerm.replace(' ', '-').toLowerCase();

			if (this.cachedResults[queryKey]) {
				this.renderSearchResults(this.cachedResults[queryKey]);

				return;
			}

			fetch(
				`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent(
					'resources[type]'
				)}=product,page,article,collection&${encodeURIComponent('resources[limit]')}=10&${encodeURIComponent(
					'resources[limit_scope]'
				)}=each&section_id=predictive-search`
			)
				.then((response) => {
					if (!response.ok) {
						let error = new Error(response.status);

						this.close();

						throw error;
					}

					return response.text();
				})
				.then((text) => {
					const resultsMarkup = new DOMParser()
						.parseFromString(text, 'text/html')
						.querySelector('#shopify-section-predictive-search').innerHTML;

					this.cachedResults[queryKey] = resultsMarkup;
					this.renderSearchResults(resultsMarkup);
				})
				.catch((error) => {
					this.close();

					throw error;
				});
		}

		renderSearchResults(resultsMarkup) {
			this.elements.predictiveSearchResults.innerHTML = resultsMarkup;
			this.querySelector(this.selectors.tabsMenu).innerHTML = '';
			this.querySelector(this.selectors.tabsMenu).append(
				this.elements.predictiveSearchResults.querySelector(this.selectors.predictiveSearchTabs)
			);

			this.elements.predictiveSearchResults
				.querySelectorAll(this.selectors.tabsContent)
				.forEach((link, index) => {
					if (index === 0) {
						link.classList.add(this.classes.open);
					} else {
						link.classList.remove(this.classes.open);
					}
				});

			this.querySelectorAll(this.selectors.predictiveSearchTabsLinks).forEach((link, index) => {
				if (index === 0) {
					link.classList.add(this.classes.active);
				} else {
					link.classList.remove(this.classes.active);
				}
			});

			this.setAttribute('results', true);

			const imagesEvent = new CustomEvent('lazy-image:reload', {
				bubbles: true,
				detail: this.elements.predictiveSearchResults,
			});

			const videosEvent = new CustomEvent('videos:reload', {
				bubbles: true,
				detail: this.elements.predictiveSearchResults,
			});

			document.dispatchEvent(imagesEvent);
			document.dispatchEvent(videosEvent);
		}

		open() {
			this.setAttribute('open', true);
			this.elements.input.setAttribute('aria-expanded', true);
			this.isOpen = true;
		}

		close(clearSearchTerm = false) {
			if (clearSearchTerm) {
				this.elements.input.value = '';
				this.removeAttribute('results');
			}

			const selected = this.querySelector('[aria-selected="true"]');

			if (selected) selected.setAttribute('aria-selected', false);

			this.elements.input.value = '';
			this.elements.input.setAttribute('aria-activedescendant', '');
			this.elements.input.setAttribute('aria-expanded', false);
			this.removeAttribute('open');
			this.removeAttribute('results');

			if (this.isEnabled) {
				this.elements.predictiveSearchResults.innerHTML = this.initialContent;
				this.querySelector(this.selectors.tabsMenu).innerHTML = '';
			}

			this.isOpen = false;
		}
	}

	if (typeof customElements.get('predictive-search') === 'undefined') {
		customElements.define('predictive-search', PredictiveSearch);
	}
}
